/* eslint-disable max-len */
/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable no-nested-ternary */
import React, { useState, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, Box } from '@mui/material';
// eslint-disable-next-line import/no-extraneous-dependencies
import dayjs from 'dayjs';
// eslint-disable-next-line import/no-extraneous-dependencies
import { AlertTriangle, Clock, Calendar } from 'lucide-react';
import { sendRequest } from '../../api/shootAPI';
import API from '../../api/endpoints';
import { UserContext } from '../../contexts/User';
import { NotificationContext } from '../../contexts/Notification';
import DatePicker from '../../components/datePicker';
import { getTimeString } from '../../utils/time';
import { STYLED_SELECT_CLASS as selectClass, STYLED_SELECT_STYLE as selectStyle } from '../../utils/styledSelect';
import './RequestModal.css';

const moment = require('moment-timezone');

const TIME_HOURS = Array.from({ length: 12 }, (_, i) => i + 1); // 1..12
const TIME_MINUTES = Array.from({ length: 12 }, (_, i) => i * 5); // 0,5,...,55 (step 5)

// Green calendar caret for the date inputs, matching the prototype / date-range picker.
function CalendarCaret(props) {
  // eslint-disable-next-line react/jsx-props-no-spreading
  return <Calendar {...props} size={16} color="#53A551" />;
}

// Segmented time field (hour / minute / AM-PM) styled to the prototype, with a
// clock icon. Reads/emits a dayjs value so the existing handlers stay untouched.
function TimeField({ value, onChange }) {
  const d = dayjs.isDayjs(value) ? value : dayjs(value);
  const h24 = d.hour();
  const minute = d.minute();
  const period = h24 >= 12 ? 'PM' : 'AM';
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  const minuteVal = TIME_MINUTES.includes(minute) ? minute : (Math.round(minute / 5) * 5) % 60;

  const emit = (nh12, nm, np) => {
    let h = nh12 % 12;
    if (np === 'PM') h += 12;
    onChange(d.hour(h).minute(nm).second(0));
  };

  const segCls = 'appearance-none bg-transparent border-0 text-sm text-ink-900 focus:outline-none tabular-nums font-medium cursor-pointer';
  const segStyle = { backgroundImage: 'none', paddingRight: 0 };

  return (
    <div
      className="flex items-center gap-1 h-10 px-3 border border-rule rounded-[8px] bg-white w-full"
      style={{ borderStyle: 'solid' }}
    >
      <select aria-label="Hour" value={h12} onChange={(e) => emit(Number(e.target.value), minuteVal, period)} className={segCls} style={segStyle}>
        {TIME_HOURS.map((h) => (<option key={h} value={h}>{String(h).padStart(2, '0')}</option>))}
      </select>
      <span className="text-ink-900 font-medium">:</span>
      <select aria-label="Minute" value={minuteVal} onChange={(e) => emit(h12, Number(e.target.value), period)} className={segCls} style={segStyle}>
        {TIME_MINUTES.map((m) => (<option key={m} value={m}>{String(m).padStart(2, '0')}</option>))}
      </select>
      <select aria-label="Period" value={period} onChange={(e) => emit(h12, minuteVal, e.target.value)} className={segCls} style={segStyle}>
        <option value="AM">AM</option>
        <option value="PM">PM</option>
      </select>
      <Clock size={16} className="text-ink-500 ml-auto flex-shrink-0" />
    </div>
  );
}

TimeField.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  value: PropTypes.object,
  onChange: PropTypes.func.isRequired,
};

TimeField.defaultProps = { value: null };

function RequestModal({
  closeModal, policies, setLoading, teamMembers, isManagerInitiated, editRequest,
}) {
  const { member, activeWorkspace, user } = useContext(UserContext);
  const {
    displayNotification,
  } = useContext(NotificationContext);

  const getHours = (value) => {
    const inputs = value.split(':');
    if (inputs[0] && inputs[1]) {
      return (parseInt(inputs[0], 10) * 60 + parseInt(inputs[1], 10));
    }
    return 0;
  };

  // Edit mode: the same form, pre-filled from an existing request and saved via PUT.
  const isEdit = Boolean(editRequest);
  const editTotalMin = editRequest?.totalHours || 0;
  // A "full day" is 7.5h (450 min). Treat the request as Full Days only when the
  // total is a clean multiple of that (e.g. 15h = 2 days); otherwise it's a
  // specific-hours request — never show fractional days like 1.066…
  const editIsFullDay = editTotalMin > 0 && editTotalMin % 450 === 0;

  const [selectedMember, setSelectedMember] = useState(
    teamMembers && teamMembers.length > 0 ? teamMembers[0].id : member.id,
  );
  const [memberPolicies, setMemberPolicies] = useState([]);
  const [policy, setPolicy] = useState(editRequest?.policyId || 0);
  const [startDate, setStartDate] = useState(editRequest ? new Date(editRequest.startDate) : new Date());
  const [endDate, setEndDate] = useState(editRequest ? new Date(editRequest.endDate) : new Date());
  const [allDay, setAllDay] = useState(isEdit ? editIsFullDay : true);

  const [ptoValue, setPtoValue] = useState(
    isEdit ? (editIsFullDay ? String(editTotalMin / 450) : String(editTotalMin / 60)) : '1.0',
  );
  const [memberBalances, setMemberBalances] = useState({});

  const [totalHours, setTotalHours] = useState(isEdit ? getTimeString(editTotalMin) : '07:30');
  const [reason, setReason] = useState(editRequest?.reason || '');
  const [currentBalance, setCurrentBalance] = useState(getTimeString(0));
  const [pendingHours, setPendingHours] = useState(getTimeString(0));
  const [remaining, setRemaining] = useState(getTimeString(0));
  const [selectedTimezone, setSelectedTimezone] = useState(
    editRequest?.timezone || activeWorkspace?.timezone || user?.timezone || 'UTC',
  );
  const [startTime, setStartTime] = useState(
    isEdit && !editIsFullDay ? dayjs(editRequest.startDate) : dayjs().hour(9).minute(0).second(0),
  );
  const [endTime, setEndTime] = useState(
    isEdit && !editIsFullDay ? dayjs(editRequest.endDate) : dayjs().hour(16).minute(30).second(0),
  );

  const handleAllDayChange = (checked) => {
    setAllDay(checked);

    if (checked) {
      setPtoValue('1');
      setTotalHours('07:30');
      setEndDate(startDate);
    } else {
      if (startTime && endTime) {
        const diffMinutes = endTime.diff(startTime, 'minutes');
        const diffHours = diffMinutes / 60;

        if (diffHours > 0) {
          setPtoValue(diffHours.toFixed(2));
          const hoursStr = Math.floor(diffHours).toString().padStart(2, '0');
          const minutesStr = (diffMinutes % 60).toString().padStart(2, '0');
          setTotalHours(`${hoursStr}:${minutesStr}`);
        } else {
          setPtoValue('0');
          setTotalHours('00:00');
        }
      } else {
        setPtoValue('0');
        setTotalHours('00:00');
      }
      setEndDate(startDate);
    }
  };
  const handleStartTimeChange = (newValue) => {
    setStartTime(newValue);

    if (!allDay && newValue) {
      const hours = parseFloat(ptoValue || 0);
      if (hours > 0) {
        const newEndTime = newValue.add(hours, 'hour');
        setEndTime(newEndTime);
      }
    }
  };

  const handleEndTimeChange = (newValue) => {
    setEndTime(newValue);
    if (!allDay && startTime && newValue) {
      const diffHours = newValue.diff(startTime, 'minutes') / 60;

      if (diffHours > 0) {
        setPtoValue(diffHours.toFixed(2));
        const hoursStr = Math.floor(diffHours).toString().padStart(2, '0');
        const minutesStr = Math.floor((diffHours % 1) * 60).toString().padStart(2, '0');
        setTotalHours(`${hoursStr}:${minutesStr}`);
      }
    }
  };

  const handlePtoValueChange = (event) => {
    const { value } = event.target;
    setPtoValue(value);

    if (allDay) {
      const hours = parseFloat(value || 0) * 7.5;
      const hoursStr = Math.floor(hours).toString().padStart(2, '0');
      const minutesStr = Math.floor((hours % 1) * 60).toString().padStart(2, '0');
      setTotalHours(`${hoursStr}:${minutesStr}`);

      const days = parseInt(value || 1, 10) - 1;
      const newEndDate = new Date(startDate);
      newEndDate.setDate(startDate.getDate() + days);
      setEndDate(newEndDate);
    } else {
      const hours = parseFloat(value || 0);
      const hoursStr = Math.floor(hours).toString().padStart(2, '0');
      const minutesStr = Math.floor((hours % 1) * 60).toString().padStart(2, '0');
      setTotalHours(`${hoursStr}:${minutesStr}`);

      if (startTime && hours > 0) {
        const newEndTime = startTime.add(hours, 'hour');
        setEndTime(newEndTime);
      }
    }
  };

  const handleStartDateChange = (date) => {
    setStartDate(date);

    if (allDay) {
      const days = parseInt(ptoValue || 1, 10) - 1;
      const newEndDate = new Date(date);
      newEndDate.setDate(date.getDate() + days);
      setEndDate(newEndDate);
    } else {
      const timeDiff = Math.abs(endDate.getTime() - date.getTime());
      const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1;

      if (daysDiff > 0) {
        setAllDay(true);
        setPtoValue(daysDiff.toString());
        const hours = daysDiff * 7.5;
        const hoursStr = Math.floor(hours).toString().padStart(2, '0');
        const minutesStr = Math.floor((hours % 1) * 60).toString().padStart(2, '0');
        setTotalHours(`${hoursStr}:${minutesStr}`);
      }
    }
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);

    const timeDiff = Math.abs(date.getTime() - startDate.getTime());
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1;

    if (daysDiff > 0) {
      setAllDay(true);
      setPtoValue(daysDiff.toString());
      const hours = daysDiff * 7.5;
      const hoursStr = Math.floor(hours).toString().padStart(2, '0');
      const minutesStr = Math.floor((hours % 1) * 60).toString().padStart(2, '0');
      setTotalHours(`${hoursStr}:${minutesStr}`);
    }
  };

  useEffect(() => {
    if (isEdit) return; // keep the pre-filled values when editing
    if (allDay) {
      setPtoValue('1');
      setTotalHours('07:30');
    } else {
      setPtoValue('7.50');
      setTotalHours('07:30');
    }
  }, []);

  // Fetch policies and balances when member changes
  useEffect(() => {
    const fetchMemberData = async () => {
      const targetMemberId = isManagerInitiated ? selectedMember : member.id;
      if (!targetMemberId || !member.workspaceId) return;

      // Fetch policies for the selected member
      const policyResponse = await sendRequest({
        method: 'get',
        url: API.WORKSPACE.getOffTimeData(member.workspaceId, targetMemberId),
      });

      if (policyResponse.data && policyResponse.data.ptoPolicies) {
        setMemberPolicies(policyResponse.data.ptoPolicies);
        // Don't override the request's own policy when editing.
        if (!isEdit && policyResponse.data.ptoPolicies.length > 0) {
          setPolicy(policyResponse.data.ptoPolicies[0].id);
        }
      }

      // Fetch balances for the selected member
      const balanceResponse = await sendRequest({
        method: 'get',
        url: API.WORKSPACE.getPTOBalances(member.workspaceId, targetMemberId),
      });

      if (balanceResponse.success) {
        setMemberBalances(balanceResponse.data.balanceHours || {});

        if (balanceResponse.data.pendingHours !== undefined) {
          setPendingHours(getTimeString(balanceResponse.data.pendingHours / 60));
        } else {
          setPendingHours(getTimeString(0));
        }
      } else {
        setMemberBalances({});
      }
    };
    fetchMemberData();
  }, [isManagerInitiated, selectedMember, member]);

  useEffect(() => {
    const requested = getHours(totalHours);

    let balanceData = null;
    if (memberBalances) {
      balanceData = memberBalances[policy] || memberBalances[String(policy)];
    }

    if (balanceData) {
      const accruedMinutes = (balanceData.accruedHours || 0) / 60;
      const carriedOverMinutes = (balanceData.carriedOverHours || 0) / 60;
      const usedMinutes = (balanceData.usedHours || 0) / 60;

      const currentBalanceMinutes = accruedMinutes + carriedOverMinutes - usedMinutes;
      setCurrentBalance(getTimeString(currentBalanceMinutes));
      const pending = getHours(pendingHours);
      const remainingMinutes = currentBalanceMinutes - requested - pending;
      setRemaining(getTimeString(remainingMinutes));
    } else {
      setCurrentBalance(getTimeString(0));
      const pending = getHours(pendingHours);
      setRemaining(getTimeString(0 - pending - requested));
    }
  }, [policy, totalHours, memberBalances, pendingHours]);

  const handleSubmit = async () => {
    const requestedMinutes = getHours(totalHours);

    if (requestedMinutes <= 0) {
      displayNotification({
        message: 'Total Time Off must be greater than 0.',
        severity: 'error',
      });
      return;
    }

    if (!reason) {
      displayNotification({
        message: 'Time Off reason is required.',
        severity: 'error',
      });
      return;
    }

    if (remaining.includes('-')) {
      displayNotification({
        message: 'Requested time exceeds remaining balance.',
        severity: 'error',
      });
      return;
    }

    let submitStartDate; let
      submitEndDate;

    if (allDay) {
      submitStartDate = startDate;
      submitEndDate = endDate;
    } else {
      const selectedDateStr = moment(startDate).format('YYYY-MM-DD');
      const startTimeStr = dayjs(startTime).format('HH:mm:ss');
      const endTimeStr = dayjs(endTime).format('HH:mm:ss');

      submitStartDate = `${selectedDateStr}T${startTimeStr}`;
      submitEndDate = `${selectedDateStr}T${endTimeStr}`;
    }

    const submitData = {
      policyId: policy,
      totalHours: requestedMinutes,
      startDate: submitStartDate,
      endDate: submitEndDate,
      allDay,
      status: 'Submitted',
      reason,
      timezone: selectedTimezone,
      initiatedByManager: isManagerInitiated || false,
    };

    if (member) {
      const targetMemberId = isManagerInitiated ? selectedMember : member.id;
      if (setLoading) setLoading(true);
      const response = isEdit
        ? await sendRequest({
          method: 'put',
          url: API.WORKSPACE.editOffTimeRequest(member.workspaceId, editRequest.id),
          data: { ...submitData, id: editRequest.id },
        })
        : await sendRequest({
          method: 'post',
          url: API.WORKSPACE.createOffTimeRequest(member.workspaceId, targetMemberId),
          data: submitData,
        });

      if (response.success) {
        let successMessage;
        if (isEdit) {
          successMessage = 'Time off request modified successfully.';
        } else if (isManagerInitiated) {
          successMessage = 'Time off request created and auto-approved successfully.';
        } else {
          successMessage = 'Time off request submitted successfully.';
        }
        displayNotification({ message: successMessage, severity: 'success' });
        closeModal();
      } else {
        displayNotification({
          message: isEdit ? 'An error occurs while modifying data.' : 'An error occurred while submitting the request.',
          severity: 'error',
        });
      }
      if (setLoading) setLoading(false);
    }
  };

  const isOverBalance = remaining.includes('-');
  const policyOptions = isManagerInitiated ? memberPolicies : policies;
  const timezoneOptions = [...new Set(
    [selectedTimezone, activeWorkspace?.timezone, user?.timezone, 'UTC'].filter(Boolean),
  )];

  return (
    <Modal open onClose={() => closeModal(false)} className="flex-row align-c jstfy-c">
      <Box style={{
        width: 768,
        maxWidth: 'calc(100% - 32px)',
        maxHeight: '90vh',
        overflowY: 'auto',
        padding: 24,
        background: '#fff',
        borderRadius: 12,
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
      }}
      >
        <h5 className="text-lg font-semibold leading-none text-ink-900 mb-0">
          {isEdit
            ? (editRequest.status === 'Denied' ? 'Resubmit Time Off Request' : 'Edit Time Off Request')
            : 'Request Time Off'}
        </h5>

        <div className="grid grid-cols-1 sm:grid-cols-[1fr_240px] gap-6">
          <div className="space-y-4">
            {isManagerInitiated && teamMembers && teamMembers.length > 0 && (
              <div>
                <label className="block text-xs text-ink-500 font-medium mb-1.5">Select Member</label>
                <select
                  value={selectedMember}
                  onChange={(e) => setSelectedMember(Number(e.target.value))}
                  className={`${selectClass} w-full`}
                  style={selectStyle}
                >
                  {teamMembers.map((tm) => (
                    <option key={tm.id} value={tm.id}>
                      {`${tm.user?.firstName || ''} ${tm.user?.lastName || ''}`.trim()}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label className="block text-xs text-ink-500 font-medium mb-1.5">Policy</label>
              <select
                value={policy}
                onChange={(e) => setPolicy(Number(e.target.value))}
                className={`${selectClass} w-full`}
                style={selectStyle}
              >
                {policyOptions.map((item) => (
                  <option key={item.id} value={item.id}>{item.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs text-ink-500 font-medium mb-1.5">Time Span</label>
              <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
                <DatePicker oneTap value={startDate} onChange={handleStartDateChange} caretAs={CalendarCaret} />
                <span className="text-sm text-ink-500">to</span>
                <DatePicker oneTap value={endDate} onChange={handleEndDateChange} caretAs={CalendarCaret} />
              </div>
            </div>

            {!allDay && (
              <div>
                <label className="block text-xs text-ink-500 font-medium mb-1.5">Time Range</label>
                <div className="grid grid-cols-[auto_1fr_auto_1fr] items-center gap-2">
                  <span className="text-sm text-ink-500">From</span>
                  <TimeField value={startTime} onChange={handleStartTimeChange} />
                  <span className="text-sm text-ink-500">To</span>
                  <TimeField value={endTime} onChange={handleEndTimeChange} />
                </div>
              </div>
            )}

            <div>
              <label className="block text-xs text-ink-500 font-medium mb-1.5">Timezone</label>
              <select
                value={selectedTimezone}
                onChange={(e) => setSelectedTimezone(e.target.value)}
                className={`${selectClass} w-full`}
                style={selectStyle}
              >
                {timezoneOptions.map((tz) => (
                  <option key={tz} value={tz}>{tz}</option>
                ))}
              </select>
            </div>

            <div>
              <p className="text-xs text-ink-500 font-medium mb-2">Request Type:</p>
              <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={allDay}
                  onChange={(e) => handleAllDayChange(e.target.checked)}
                  className="w-4 h-4 accent-[#0e1e40] cursor-pointer"
                />
                <span className="text-sm text-ink-900 font-medium">Full Days (7.5 hours per day)</span>
              </label>
              {!allDay && (
                <p className="text-[11px] leading-4 text-ink-500 mt-2">Unchecked = Request specific hours</p>
              )}
            </div>

            <div>
              <label className="block text-xs text-ink-500 font-medium mb-1.5">
                {allDay ? 'Number of Days' : 'Number of Hours'}
              </label>
              <input
                type="number"
                min={allDay ? 0.5 : 0.25}
                step={allDay ? 0.5 : 0.25}
                value={ptoValue}
                onChange={handlePtoValueChange}
                className="w-full h-10 px-3 border border-rule rounded-[8px] text-sm bg-white text-ink-900 focus:outline-none focus:border-brand-500"
                style={{ borderStyle: 'solid' }}
              />
              <p className="text-[11px] leading-4 text-ink-500 mt-2">
                {allDay
                  ? `${ptoValue ? (parseFloat(ptoValue) * 7.5).toFixed(1) : 0} hours total (1 day = 7.5 hours)`
                  : 'Enter hours (e.g., 4.5 for 4 hours 30 minutes)'}
              </p>
            </div>

            <div>
              <label className="block text-xs text-ink-500 font-medium mb-1.5">Total Time Off Hours</label>
              <div
                className="w-full h-10 px-3 border border-rule rounded-[8px] text-sm bg-surface-page text-ink-900 flex items-center tabular-nums"
                style={{ borderStyle: 'solid' }}
              >
                {totalHours}
              </div>
            </div>

            <div>
              <label className="block text-xs text-ink-500 font-medium mb-1.5">Time Off Reason</label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                placeholder="Required"
                className="w-full px-3 py-2 border border-rule rounded-[8px] text-sm bg-white text-ink-900 placeholder:text-ink-500 focus:outline-none focus:border-brand-500 resize-none"
                style={{ borderStyle: 'solid' }}
              />
            </div>
          </div>

          <div className="border-l border-surface-1 pl-6 space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-ink-500">Current Balance</span>
              <span className={`font-medium tabular-nums ${currentBalance.includes('-') ? 'text-[#CC4525]' : 'text-ink-900'}`}>
                {currentBalance}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-ink-500">{allDay ? 'Days Requested' : 'Hours Requested'}</span>
              <span className="text-brand-500 font-semibold tabular-nums">
                {allDay
                  ? `${ptoValue} day${parseFloat(ptoValue) !== 1 ? 's' : ''}`
                  : `${ptoValue} hours`}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-ink-500">Other Pending Approval</span>
              <span className={`font-semibold tabular-nums ${pendingHours !== '00:00' ? 'text-[#F19235]' : 'text-ink-500'}`}>
                {pendingHours}
              </span>
            </div>
            <div className="border-t border-surface-1 pt-3 flex items-center justify-between text-sm">
              <span className="text-ink-900 font-semibold">Remaining Balance</span>
              <span className={`font-semibold tabular-nums ${isOverBalance ? 'text-[#CC4525]' : 'text-[#53A551]'}`}>
                {remaining}
              </span>
            </div>
            {isOverBalance && (
              <div className="flex items-start gap-2 px-3 py-2.5 rounded-[8px] bg-[#fdebe6] text-[#CC4525] text-xs leading-relaxed">
                <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
                <span>This request exceeds your available balance</span>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-start items-center gap-2">
          <button
            type="button"
            onClick={() => closeModal(false)}
            className="inline-flex items-center justify-center px-4 py-2 border border-rule rounded-[8px] text-sm font-medium text-[#53A551] bg-white hover:bg-[#eef5ea] transition"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="inline-flex items-center justify-center px-5 py-2 bg-[#53A551] text-white rounded-[8px] text-sm font-semibold hover:bg-[#458a44] transition"
          >
            {isEdit
              ? (editRequest.status === 'Denied' ? 'Resubmit Request' : 'Save Changes')
              : 'Submit Request'}
          </button>
        </div>
      </Box>
    </Modal>
  );
}

RequestModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  policies: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
  })),
  setLoading: PropTypes.func,
  teamMembers: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    user: PropTypes.shape({
      firstName: PropTypes.string,
      lastName: PropTypes.string,
    }),
  })),
  isManagerInitiated: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  editRequest: PropTypes.object,
};

RequestModal.defaultProps = {
  policies: [],
  setLoading: null,
  teamMembers: [],
  isManagerInitiated: false,
  editRequest: null,
};

export default RequestModal;
